import type { ProjectNavKey } from './ProjectHomeNav';

/**
 * ⭐ **프로젝트 2차 네비가 «어디로 가는가» — 한 곳** (2026-09-30).
 *
 * ## 왜 모으나
 *
 * 네비를 홈 밖(하위 네 화면)에도 두면 «누르는 곳»이 둘이 된다. 목적지를 양쪽에 적으면
 * 곧 갈라진다 — 이 레포는 그 갈라짐을 이미 한 번 겪었다:
 * ```
 *   2026-07-25 — 「내 프로젝트」의 목적지가 /project/projects → /task 로 바뀌었다
 *   ⇒ 그때 «한 곳»만 고쳤다면 다른 곳은 옛 주소로 남았을 것이다
 * ```
 * ⛔ 그리고 옛 가지(`feature/dev-syk`)를 그대로 베끼면 **main 에 없는 곳으로 간다**:
 * ```
 *   dev-syk 가 가는 곳                main 의 짝
 *   /project/space/:teamId            ⛔ main 에 그 라우트가 «없다»
 *   /project?view=projects            ⛔ main 의 ProjectPage 는 ?view= 를 «안 읽는다»
 * ```
 * ⇒ ★ 그래서 목적지는 **main 의 `ProjectPage` 가 쓰던 것**을 그대로 옮겨 여기 한 벌로 둔다.
 *
 * ## ⚠ 순수 함수다 — 라우터를 모른다
 *
 * `navigate()` 를 부르지 않고 «주소 글자»만 돌려준다. 그래야 화면을 안 그리고도 잰다.
 */

/**
 * 2차 네비 항목을 눌렀을 때 갈 곳. 「내 워크스페이스」는 펼침이라 이 길로 오지 않는다.
 *
 * ## ⛔⛔ 이 함수의 ★ 「떨어지는 끝」이 ⭐ 함정이다
 *
 * 맨 아래가 `return '/project/teams'` 라 **새 열쇠를 더하고 분기를 잊으면**
 * ⇒ ⛔ 그 열쇠가 ★ «조용히 `/project/teams` 로 간다» — 던지지도, 경고하지도 않는다.
 * ⇒ ★ 2026-10-07 에 `'home'` 을 더할 때 ⭐ 그것을 밟을 수 있었다.
 * ⇒ ⇒ ★ 그래서 `project-nav-targets.test.ts` 가 ⭐ **`PROJECT_NAV_KEYS` 를 돌며
 *   「목적지가 ★ 서로 «다른가»」**를 센다 — 떨어지면 두 열쇠가 같은 값이 되어 ★ 걸린다.
 * ⚠ 끝을 `throw` 로 바꾸지 않았다 — 기존 줄을 바꾸는 일이고(회귀 금지), ⭐ 위 시험이
 *   그 구멍을 «바깥에서» 막는다.
 */
export function projectNavTarget(key: ProjectNavKey): string {
  // 2026-10-07 — 「홈」은 프로젝트 홈(/project). 이 메뉴가 하위 화면 전부에 뜨므로
  //   거기서 홈으로 돌아오는 «일관된» 길이 된다.
  if (key === 'home') return '/project';
  // 2026-07-25 — 「내 프로젝트」는 목록(/project/projects)이 아니라 3d(/task) 로 간다.
  if (key === 'projects') return '/task';
  if (key === 'my-tasks') return '/project/my-tasks';
  return '/project/teams';
}

/** 네비의 워크스페이스 한 줄을 눌렀을 때. 팀을 쿼리로 실어 그 맥락으로 연다. */
export function projectTeamTarget(teamId: string): string {
  return `/project/teams?team=${teamId}`;
}

/**
 * 스페이스 «안»(그 스페이스의 프로젝트 목록)을 연다 — 2026-10-13 정본 맞춤.
 *
 * 정본(dd05a6fcd)은 홈 카드 · 새 스페이스 생성 직후 · 좌측 메뉴의 스페이스 한 줄을 모두
 * `/project/space/{id}` 로 보낸다. main 은 그 라우트가 없던 때(09-30) 위 `projectTeamTarget`
 * (관리 화면)으로 대신했고, 10-08 에 라우트(SpaceV2Page)가 들어온 뒤에도 그대로였다
 * ⇒ 사용자 지적 「스페이스를 생성했는데 스페이스에 목록이 나타나지 않는다」.
 *
 * 깃발 `PROJECT_V2` 는 여기서 읽지 않는다 — 그 분기는 문(ProjectV2Route) 하나만 한다
 * (project-v2-flag.guard ①). 꺼진 빌드에선 그 라우트의 fallback(SpaceV2Fallback)이
 * 종전 목적지 `projectTeamTarget` 으로 넘긴다.
 * `projectTeamTarget` 은 그대로 둔다 — 검색 결과·팀 배지 등은 계속 관리 화면으로 간다.
 */
export function projectSpaceTarget(teamId: string): string {
  return `/project/space/${teamId}`;
}

/*
 * ⛔⛔ 2026-10-12 — ★ 「통계 상세로 가는 길」을 ⭐ «걷었다».
 *
 * 사용자 결정 ⇒ 「1. /project/stats 삭제해라 ★ 화면도 필요없다.」
 *
 * ## ⚠ 종전에 이 자리가 무엇이었나 — 지우지 않고 적어 둔다
 *
 * 2026-10-07 에 `projectStatsTarget()` 이 여기 있었고, 그 머리말은 이렇게 적고 있었다:
 *   「이 주소는 라우터에 «있었는데» 화면에서 가는 길이 0곳이었다 ⇒ 주소를 직접 쳐야만
 *    열리는 화면이었다 ⇒ 그래서 운영이 그리는 갈래(3c 위젯 홈)의 주간 완료율 위젯에
 *    길을 붙였다」 ⇒ ⇒ ★ 그 방향이 ⭐ 뒤집혔다 — 입구를 만드는 것이 아니라 «화면을 걷는다».
 * ⇒ ⚠ 그때 근거로 든 설계 문서(docs/design/architecture-project-menu.md §3.1 의
 *   「PP -->|주간 완료율 카드| SD」)도 ★ 사용자 결정보다 «앞서지 않는다».
 *
 * ⛔ 정본(feature/dev-syk)에는 그 화면이 «있다»(router 에도 1).
 *   ⇒ ⇒ 그래도 ★ 사용자 결정이 앞선다 — ⛔ 「정본에 있으니 이관해야 한다」로 되살리지 말 것.
 *   ⇒ ⇒ ⇒ ⚠ 「홈」 항목이 그렇게 왔다 갔다 했다(2026-10-10 사용자 ⇒ 「홈 지워」).
 * ⇒ 되살아나지 않게 `project-stats-removed.guard.test.ts` 가 잠근다.
 */

/**
 * ⭐ **통합 검색 결과 화면으로 가는 길** (2026-10-07 · 사용자 결정 ㉠-2 — 「화면 이동」).
 *
 * ## ⚠ 반드시 «인코딩»한다
 *
 * ```
 * 질의에 들어올 수 있는 것: 한글 · 공백 · & · # · = · +
 * ⛔ 그대로 이으면 ⇒ `?q=a&b` 가 ★ 칸 둘로 읽히고, `#` 뒤는 ⭐ 서버에 아예 안 간다.
 * ⇒ ★ encodeURIComponent 하나로 넷 다 막힌다(＋ 는 %2B 가 되어 공백으로 안 읽힌다).
 * ```
 * ⚠ 빈 질의로는 **이 길을 쓰지 않는다** — 쓰는 쪽이 「찾기」를 막는다. 그래도 빈 글자가 오면
 *   `?q=` 로 가고, 결과 화면은 그때 **묶음을 안 그린다**(「해당 없음」이라 말하지 않는다).
 */
export function projectSearchTarget(q: string): string {
  return `/project/search?q=${encodeURIComponent(q.trim())}`;
}
